// CHUNK 13: Client-side Instagram Story image generator using HTML5 Canvas API.
// Outputs a 1080×1920px PNG matching Professya brand colors.

export interface StoryData {
  personAName:  string;
  personBName:  string;
  starScore:    number; // 1–5
  synastryType: string;
  topInsight?:  string;
}

const COLORS = {
  bg:       '#0D1130',
  bgDeep:   '#060812',
  gold:     '#C9A84C',
  cream:    '#F5ECD7',
  creamDim: 'rgba(245,236,215,0.6)',
  overlay:  'rgba(201,168,76,0.08)',
};

const TYPE_EMOJI: Record<string, string> = {
  romantic:     '💕',
  friendship:   '🤝',
  professional: '💼',
  family:       '✨',
};

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
): void {
  const words = text.split(' ');
  let line    = '';
  for (const word of words) {
    const test = line + word + ' ';
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line.trimEnd(), x, y);
      y   += lineHeight;
      line = word + ' ';
    } else {
      line = test;
    }
  }
  if (line.trim()) ctx.fillText(line.trimEnd(), x, y);
}

export async function generateSynastryStoryCanvas(
  canvas: HTMLCanvasElement,
  data: StoryData,
): Promise<void> {
  canvas.width  = 1080;
  canvas.height = 1920;

  const ctx = canvas.getContext('2d')!;

  // Background gradient
  const grad = ctx.createLinearGradient(0, 0, 0, 1920);
  grad.addColorStop(0, COLORS.bg);
  grad.addColorStop(1, COLORS.bgDeep);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 1080, 1920);

  // Decorative circles
  ctx.fillStyle = COLORS.overlay;
  ctx.beginPath();
  ctx.arc(180, 300, 420, 0, Math.PI * 2);
  ctx.fill();

  ctx.beginPath();
  ctx.arc(920, 1550, 480, 0, Math.PI * 2);
  ctx.fill();

  ctx.textAlign = 'center';

  // Ornament top
  ctx.fillStyle = COLORS.gold;
  ctx.font      = '80px serif';
  ctx.fillText('✦', 540, 240);

  // Title
  ctx.font      = 'bold 72px Georgia, serif';
  ctx.fillStyle = COLORS.gold;
  ctx.fillText('Nossa Conexão', 540, 360);

  // Type emoji
  const emoji = TYPE_EMOJI[data.synastryType] ?? '✦';
  ctx.font     = '64px serif';
  ctx.fillText(emoji, 540, 460);

  // Names
  ctx.font      = 'bold 60px Georgia, serif';
  ctx.fillStyle = COLORS.cream;
  const namesText = `${data.personAName} × ${data.personBName}`;
  ctx.fillText(namesText, 540, 600);

  // Stars
  const stars = '⭐'.repeat(Math.min(5, Math.max(1, data.starScore)));
  ctx.font     = '80px serif';
  ctx.fillText(stars, 540, 730);

  ctx.font      = '36px Georgia, serif';
  ctx.fillStyle = COLORS.creamDim;
  ctx.fillText('compatibilidade', 540, 800);

  // Divider
  ctx.strokeStyle = `rgba(201,168,76,0.3)`;
  ctx.lineWidth   = 1;
  ctx.beginPath();
  ctx.moveTo(180, 860);
  ctx.lineTo(900, 860);
  ctx.stroke();

  // Top insight / default message
  const insight = data.topInsight
    ?? `${data.personAName} e ${data.personBName} partilham uma energia única que ilumina o caminho de ambos.`;

  ctx.font      = '42px Georgia, serif';
  ctx.fillStyle = COLORS.cream;
  wrapText(ctx, insight, 540, 950, 860, 60);

  // Divider bottom
  ctx.beginPath();
  ctx.moveTo(180, 1500);
  ctx.lineTo(900, 1500);
  ctx.stroke();

  // Brand ornament
  ctx.font      = '52px serif';
  ctx.fillStyle = COLORS.gold;
  ctx.fillText('✦', 540, 1600);

  // Brand name
  ctx.font      = 'bold 48px Georgia, serif';
  ctx.fillStyle = COLORS.gold;
  ctx.fillText('professya.com', 540, 1700);

  ctx.font      = '30px Georgia, serif';
  ctx.fillStyle = COLORS.creamDim;
  ctx.fillText('Descubra suas conexões astrológicas', 540, 1770);
}
